import type { ReactNode, Ref } from "react";
import { useId } from "react";
import { VisuallyHidden } from "../a11y/VisuallyHidden";
import { InertControl, LOCKED_CONTROL_HINT } from "./InertControl";
import styles from "./Button.module.css";

export type ButtonVariant = "primary" | "secondary" | "icon";

/**
 * `primaryText` is the default and is not a placeholder: the default primary
 * action is ink-on-paper. Corner Infrared is dosed at roughly 8 % of the
 * surface and means live work or current selection, so `signal` and
 * `interaction` are passed explicitly at the few call sites that mean it.
 */
export type ButtonTint =
  | "primaryText"
  | "signal"
  | "interaction"
  | "destructive"
  | "warning"
  | "context";

const TINT_CLASS: Readonly<Record<ButtonTint, string | undefined>> = {
  primaryText: undefined,
  signal: styles.tintSignal,
  interaction: styles.tintInteraction,
  destructive: styles.tintDestructive,
  warning: styles.tintWarning,
  context: styles.tintContext,
};

export interface ButtonProps {
  readonly children: ReactNode;
  readonly onPress?: () => void;
  readonly variant?: ButtonVariant;
  readonly tint?: ButtonTint;
  /** Primary and secondary buttons are full width by default, icon never is. */
  readonly fullWidth?: boolean;
  /** Icon buttons only: swaps the fill to the tint and the ink to canvas. */
  readonly selected?: boolean;
  /**
   * §8.6. Renders an `InertControl` instead of a button — not a disabled
   * button. The control leaves the set of activatable elements entirely.
   */
  readonly unavailable?: boolean;
  /**
   * Enabled but locked. Stays focusable and activatable-looking, dims, and
   * gains the "Unlock controls to use this action" hint. The action is
   * suppressed here rather than at the call site so no caller can dim a control
   * and still let it fire.
   */
  readonly locked?: boolean;
  /**
   * The accessible name. Required whenever the children are not plain text —
   * an icon button's glyph is decorative and says nothing to a screen reader.
   */
  readonly label?: string;
  readonly type?: "button" | "submit";
  readonly ariaPressed?: boolean;
  readonly ariaExpanded?: boolean;
  readonly ariaControls?: string;
  readonly ariaDescribedBy?: string;
  readonly ref?: Ref<HTMLButtonElement>;
  readonly className?: string;
  readonly testId?: string;
}

/**
 * Falls back to the children when they are already text, so the common case —
 * a labelled text button that becomes unavailable — needs no second copy of its
 * own label.
 */
function accessibleName(label: string | undefined, children: ReactNode): string {
  if (label !== undefined) return label;
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  return "";
}

/**
 * The button family from §6.7.
 *
 * There is no `disabled` prop. Every unavailable state in this product is one
 * of two documented shapes — inert-and-labelled, or locked-with-a-hint — and a
 * `disabled` escape hatch is how a surface ends up with a third shape that
 * assistive technology can focus and activate to no effect (§8.6).
 */
export function Button({
  children,
  onPress,
  variant = "primary",
  tint = "primaryText",
  fullWidth,
  selected = false,
  unavailable = false,
  locked = false,
  label,
  type = "button",
  ariaPressed,
  ariaExpanded,
  ariaControls,
  ariaDescribedBy,
  ref,
  className,
  testId,
}: ButtonProps) {
  const hintId = useId();

  if (unavailable) {
    return (
      <InertControl
        title={accessibleName(label, children)}
        fullWidth={fullWidth ?? variant !== "icon"}
        className={className}
        testId={testId}
      >
        {children}
      </InertControl>
    );
  }

  const isFullWidth = fullWidth ?? variant !== "icon";
  const classes = [
    styles.button,
    styles[variant],
    TINT_CLASS[tint],
    variant === "icon" && selected ? styles.iconSelected : undefined,
    isFullWidth && variant !== "icon" ? styles.fullWidth : undefined,
    locked ? styles.locked : undefined,
    className,
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  const describedBy = [ariaDescribedBy, locked ? hintId : undefined]
    .filter((value): value is string => Boolean(value))
    .join(" ");

  return (
    <>
      <button
        ref={ref}
        type={type}
        className={classes}
        data-testid={testId}
        aria-label={label}
        aria-pressed={ariaPressed}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        aria-describedby={describedBy === "" ? undefined : describedBy}
        onClick={locked ? undefined : onPress}
      >
        {children}
      </button>
      {locked && <VisuallyHidden id={hintId}>{LOCKED_CONTROL_HINT}</VisuallyHidden>}
    </>
  );
}
